import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Container,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { formatDate2, fetch } from '../../utils/lib';
import ListItemSupplyEdit from '../../layouts/ListItemSupplyEdit';
import InventoryIcon from '@mui/icons-material/Inventory';  
import axios from 'axios';
import { useTranslation } from 'react-i18next';

function ViewPetSupply({pet}) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [supply, setSupply] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [supplyDialog, setSupplyDialog] = useState({ open: false, id: null });
  const [hasMore, setHasMore] = useState(true);
  const observerRef = useRef(null);

  useEffect(() => {
    setIsLoading(true);
    const fetchSupply_ = async () => {
      const response = await fetch('pets_supply', { id_pet: pet.id, page: page });
      setSupply(prev => page === 1 ? response.content : [...prev, ...response.content]);
      setHasMore(response.total_pages > page);
      setIsLoading(false);
    };
    fetchSupply_();
  }, [page]);

  const lastElementRef = useCallback((node) => {
    if (isLoading) return;
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore) {
        setPage((prevPage) => prevPage + 1);
      }
    });

    if (node) observerRef.current.observe(node);
  }, [isLoading, hasMore]);

  const handleMarkSuppliedClick = (id) => {
    setSupplyDialog({ open: true, id: id });
  };

  const handleMarkSuppliedConfirm = async () => {
    try {
      setIsLoading(true);
      let response = await axios.put(
        `${import.meta.env.VITE_API_URL}pets_supply?id=${supplyDialog.id}&check`,
        { id: supplyDialog.id },
        { withCredentials: true }
      );

      // Actualizar la lista después de marcar como suministrado
      const updatedSupply = supply.map(s => {
        if (s.id === supplyDialog.id) {
          return { ...s, date_supply: new Date().toISOString().split('T')[0] };
        }
        return s;
      });
      setSupply(updatedSupply);
    } catch (error) {
      console.error('Error al marcar como suministrado:', error);
    } finally {
      setIsLoading(false);
      setSupplyDialog({ open: false, id: null });
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteDialog({ open: true, id: id });
  };

  const handleDeleteConfirm = async () => {
    try {
      setIsLoading(true);
      let response = await axios.delete(
        `${import.meta.env.VITE_API_URL}pets_supply?id=${deleteDialog.id}`,
        { withCredentials: true }
      );

      // Actualizar la lista después de eliminar
      setSupply(supply.filter(s => s.id !== deleteDialog.id));
    } catch (error) {
      console.error('Error al eliminar:', error);
    } finally {
      setIsLoading(false);
      setDeleteDialog({ open: false, id: null });
    }
  };

  const handleCloseDialogs = () => {
    setDeleteDialog({ open: false, id: null });
    setSupplyDialog({ open: false, id: null });
  };

  if (isLoading && supply.length === 0) return (
    <>
      <CircularProgress
        size={42}
        sx={{ position: 'absolute', top: '50%', left: '50%', marginTop: '-21px', marginLeft: '-21px' }}
      />
    </>
  );

  if (!isLoading && supply.length === 0) return (
    <Container maxWidth="xl" sx={{ mt: 4 }}>
      <Box>
        <Typography
          variant="subtitle1"
          >
            {t('view.emptyTabCreateHint')}
        </Typography>
      </Box>
    </Container>
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 4 }} >
      {supply.map((item, index) => (
        <ListItemSupplyEdit
          key={index}
          product={item.product?.name}
          description={`${formatDate2(item.date_tentative)} - ${item.date_supply ? t('reports.supplied') : t('reports.notSupplied')}`}
          thumbUrl={item.product?.thumb_url}
          avatarContent={<InventoryIcon sx={{ fontSize: 20 }} />}
          onMarkSupplied={!item.date_supply ? () => handleMarkSuppliedClick(item.id) : undefined}
          onDelete={() => handleDeleteClick(item.id)}
          dateSupply={item.date_supply}
          dateTentative={item.date_tentative} 
          />
      ))}

      {isLoading && supply.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      <div ref={lastElementRef} />

      {/* Diálogo de confirmación para eliminar */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleCloseDialogs}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t('reports.deleteSupplyConfirm')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t('reports.deleteSupplyIrreversible')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs} color="primary">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de confirmación para marcar como suministrado */}
      <Dialog
        open={supplyDialog.open}
        onClose={handleCloseDialogs}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t('reports.confirmSupply')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t('reports.confirmSupplyDesc')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs} color="primary">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleMarkSuppliedConfirm} color="primary" autoFocus>
            {t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default ViewPetSupply;

